import { DefaultError, useMutation, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

/**
 * useMutation drop-in. **서버에 새로 접수하는** 뮤테이션(발송·생성·제출·결제 등)의 중복 실행 방지용.
 *
 * 같은 변수(variables)로 진행 중(in-flight)일 때 mutateAsync/mutate 를 다시 호출하면 새 요청을 보내지 않고
 * 진행 중인 Promise 를 그대로 반환/무시한다. 버튼 disabled/loading 은 상태 리렌더가 비동기라 같은 틱의
 * 더블클릭·연타를 못 막지만, 이 가드는 동기 ref 라 그것도 막는다. (React Query mutation 은 in-flight 중복을
 * 자동 dedupe 하지 않음.)
 *
 * dedupe 는 **변수 단위**다: 진행 중이어도 다른 변수로 호출하면 정상적으로 새 요청을 보낸다. 따라서
 * 서로 다른 대상에 동시 호출(예: 중복확인 A/B, 예약 삭제 A/B)이나 훅 인스턴스 공유 상황에서도
 * 안전하며, "같은 발송을 두 번" 만 막는다. 변수는 JSON 직렬화로 키를 만든다(직렬화 불가 시 전부 dedupe).
 */
const serializeKey = (variables: unknown): string => {
  try {
    return JSON.stringify(variables ?? null);
  } catch {
    return '__unserializable__';
  }
};

export const useGuardedMutation = <TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const mutation = useMutation(options);
  const inflight = useRef<Map<string, Promise<TData>>>(new Map());

  const mutateAsync = useCallback<UseMutationResult<TData, TError, TVariables, TContext>['mutateAsync']>(
    (variables, opts) => {
      const key = serializeKey(variables);
      const existing = inflight.current.get(key);
      if (existing) return existing;
      const p = mutation.mutateAsync(variables, opts).finally(() => {
        inflight.current.delete(key);
      });
      inflight.current.set(key, p);
      return p;
    },
    [mutation],
  );

  const mutate = useCallback<UseMutationResult<TData, TError, TVariables, TContext>['mutate']>(
    (variables, opts) => {
      if (inflight.current.has(serializeKey(variables))) return;
      void mutateAsync(variables, opts).catch(() => undefined);
    },
    [mutateAsync],
  );

  return { ...mutation, mutateAsync, mutate };
};
